<template>
  <div class="p-4 flex flex-col">
    <!-- Current hosting info  -->
    <div>
      <div v-if="selectedPanel == ''">
        <div class="flex items-center justify-between">
          <div>
            <gd-button 
              variant="outline"
              size="auto"
              @click="$emit('clickedBackButton')"
            >
              {{ $t('frontend_back') }}
            </gd-button>
          </div>

          <div>
            <gd-button
              @click="selectPanel('add_domain')"
              size="auto"
              class="px-4"
              variant="primary"
            >
              {{ $t('frontend_custom_domains_add_a_domain') }}
            </gd-button>
          </div>
        </div>
        <half-circle-spinner
          v-if="isLoading"
          style="display: block; margin: 0 auto"
          class="mt-2"
          :animation-duration="1000"
          :size="60"
          color="#3d4852"
        />
        <div v-else>
          <table
            v-if="domains.length"
            class="min-w-max w-full table-auto mt-8 border-b border-grey-200"
          >
            <thead>
              <tr
                class="
                  text-grey-500 text-sm
                  leading-normal
                  border-b border-grey-200
                "
              >
                <th class="py-3 px-4 text-left">
                  {{ $t('frontend_domain_name') }}
                </th>
                <th class="py-3 px-4 text-center">{{ $t('frontend_status') }}</th>
                <th class="py-3 px-4 text-center">{{ $t('frontend_apps') }}</th>
                <th class="py-3 px-4 text-center">
                  {{ $t('frontend_actions') }}
                </th>
              </tr>
            </thead>
            <tbody class="text-grey-800 text-sm -mx-4">
              <tr
                class="border-grey-200"
                v-for="(domain, index) in domains"
                :key="index"
                @mouseover="activeRowId = domain.id"
                @mouseleave="activeRowId = 0"
              >
                <td class="py-3 px-4 whitespace-nowrap">
                  {{ domain.domain_name }}
                </td>
                <td class="py-3 px-6 text-center">
                  <div
                    class="flex gap-1 justify-center items-center text-green-dark"
                    v-if="domain.status == 1"
                  >
                    <check-icon />
                    {{ $t('frontend_secure_connected') }}
                  </div>
                  <div
                    class="text-yellow-extra-dark flex gap-1 justify-center items-center"
                    v-else
                  >
                    <clock-icon class="fill-current h-4 w-4" />
                    {{ $t('frontend_pending_verification') }}
                  </div>
                </td>
                <td class="py-3 px-4 text-center">
                  <div v-if="domain.status == 1">
                    <div
                      v-if="domain.root_application || domain.type == 2"
                      class="flex w-full app-icons-container"
                    >
                      <div
                        class="app-icons-overlay content-center flex flex-col group items-center justify-center relative"
                      >
                        <img v-bind="getAppProps('pages', domain)" />
                        <p class="text-xs app-title group-hover:text-primary">
                          {{ brandApps?.pages?.title || 'GroovePages' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'GroovePages')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                      <div
                        class="
                          flex flex-col
                          justify-center
                          content-center
                          items-center
                          mx-2
                          app-icons-overlay
                          relative
                        "
                      >
                        <img v-bind="getAppProps('sell', domain)" />
                        <p class="text-xs app-title">
                          {{ brandApps?.sell?.title || 'GrooveSell' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'GrooveSell')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                      <div
                        class="
                          flex flex-col
                          justify-center
                          content-center
                          items-center
                          mx-2
                          app-icons-overlay
                          relative
                        "
                      >
                        <img v-bind="getAppProps('member', domain)" />
                        <p class="text-xs app-title">
                          {{ brandApps?.member?.title || 'GrooveMember' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'GrooveMember')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                      <div
                        class="
                          flex flex-col
                          justify-center
                          content-center
                          items-center
                          mx-2
                          app-icons-overlay
                          relative
                        "
                      >
                        <img v-bind="getAppProps('blog', domain)" />
                        <p class="text-xs app-title">
                          {{ brandApps?.blog?.title || 'GrooveBlog' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'GrooveBlog')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                      <div
                        class="
                          flex flex-col
                          justify-center
                          content-center
                          items-center
                          mx-2
                          app-icons-overlay
                          relative
                        "
                      >
                        <img v-bind="getAppProps('kart', domain)" />
                        <p class="text-xs app-title">
                          {{ brandApps?.kart?.title || 'Groovekart' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'GrooveKart')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                      <div
                        class="
                          flex flex-col
                          justify-center
                          content-center
                          items-center
                          mx-2
                          app-icons-overlay
                          relative
                        "
                      >
                        <img v-bind="getAppProps('ai', domain)" />
                        <p class="text-xs app-title w-24">
                          {{ brandApps?.ai?.title || 'Groove.ai' }}
                        </p>
                        <a
                          @click="fetchSubdomains(domain.id, 'Groove.ai')"
                          class="cursor-pointer flex flex-col h-8 items-center justify-center playWrapper w-8"
                        >
                          <pencil-icon class="w-5 h-5" />
                        </a>
                      </div>
                    </div>
                    <div v-else class="w-full flex flex-row">
                      <div
                        class="flex gap-2 items-center w-full mx-2"
                        v-if="
                          showRootDomainConnector &&
                            selectedRootDomainDomain.id == domain.id
                        "
                      >
                        <!-- TODO: use GdDropdown and brandData -->
                        <select
                          v-model="rootApplication.application"
                          class="
                            h-10
                            outline-none
                            rounded-lg
                            border
                            px-2
                            border-grey-200
                            mr-2
                          "
                          :value="rootApplication.application"
                          placeholder="Application"
                        >
                          <option value="pages">{{ brandApps?.pages?.title || 'GroovePages'}}</option>
                          <option value="sell-funnel">{{ brandApps?.sell?.title || 'GrooveSell'}} Branding</option>
                          <option value="sell-tracking">{{ brandApps?.sell?.title || 'GrooveSell'}} Tracking</option>
                          <option value="membership">{{ brandApps?.member?.title || 'GrooveMember'}} Membership</option>
                          <option value="portal">{{ brandApps?.member?.title || 'GrooveMember'}} Portal</option>
                          <option value="blog">{{ brandApps?.blog?.title || 'GrooveBlog'}}</option>
                          <option value="kart">{{ brandApps?.kart?.title || 'Groovekart'}}</option>
                          <option value="ai">{{ brandApps?.ai?.title || 'Groove.ai'}}</option>
                        </select>
                        <div
                          v-if="rootApplication.application == 'ai'"
                          class="mr-2 w-48"
                        >
                          <text-input
                            v-model="rootApplication.key"
                            placeholder="Whitelabel key"
                          />
                        </div>
                        <gd-button
                          variant="primary"
                          size="icon"
                          @click="saveRootdomain"
                        >
                          <check-icon />
                        </gd-button>
                        <gd-button
                          :disabled="saving"
                          @click="
                            showRootDomainConnector = false;
                            selectedRootDomainDomain = null;
                          "
                          size="icon"
                        >
                          <close-icon />
                        </gd-button>
                      </div>
                      <gd-button
                        variant="primary"
                        size="expand"
                        class="mx-4"
                        v-else
                        @click="
                          showRootDomainConnector = true;
                          selectedRootDomainDomain = domain;
                        "
                      >
                        {{ $t('frontend_custom_domains_choose_primary_app') }}
                      </gd-button>
                    </div>
                  </div>
                  <div v-else class="w-full">
                    <gd-button
                      variant="primary"
                      size="expand"
                      class="mx-4"
                      @click="openVerificationModal(domain.id)"
                    >
                      {{ $t('frontend_custom_domains_click_to_verify') }}
                    </gd-button>
                  </div>
                </td>
                <td class="py-3 px-4 text-center">
                  <div class="flex gap-2" v-if="domain.type == 1">
                    <gd-button
                      class="text-xs font-semibold"
                      variant="primary"
                      v-if="domain.status == 1"
                      size="icon"
                      @click="fetchDnsRecords(domain.id)"
                    >
                      DNS
                    </gd-button>

                    <gd-button
                      class="text-xs font-semibold"
                      variant="primary"
                      v-if="domain.status == 1"
                      size="icon"
                      @click="open404UrlModal(domain.id)"
                    >
                      404
                    </gd-button>

                    <gd-button
                      v-else
                      class="text-xs font-semibold"
                      variant="primary"
                      size="icon"
                      @click="openVerificationModal(domain.id)"
                    >
                      <check-icon />
                    </gd-button>

                    <gd-button
                      class="text-xs font-semibold"
                      variant="primary"
                      size="icon"
                      @click="deleteDomain(domain.id)"
                    >
                      <trash-icon />
                    </gd-button>
                  </div>
                  <div class="flex gap-2" v-else>
                    <gd-button
                      v-if="domain.status == 1"
                      :variant="domain.root_application ? 'base' : 'primary' "
                      size="icon"
                      @click="openRootDomainConnectorModal(domain, index)"
                      :disabled="domain.root_application ? true : false"
                    >
                      <home-icon class="h-5 w-5" />
                    </gd-button>
                    <gd-button
                      v-else
                      size="icon"
                      @click="openVerificationModal(domain.id)"
                      variant="primary"
                    >
                      <check-icon />
                    </gd-button>

                    <gd-button
                      class="text-xs font-semibold"
                      variant="primary"
                      v-if="domain.status == 1"
                      size="icon"
                      @click="open404UrlModal(domain.id)"
                    >
                      404
                    </gd-button>

                    <gd-button
                      class="text-xs font-semibold"
                      variant="primary"
                      size="icon"
                      @click="deleteDomain(domain.id)"
                    >
                      <trash-icon />
                    </gd-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="mt-8">
            <p>
              {{ $t('frontend_custom_domains_dont_have_any_domain') }}
            </p>
          </div>
        </div>
      </div>
      <div v-else-if="selectedPanel == 'add_domain'">
        <add-domain-wizard
          @clickedBackButton="selectedPanel = ''"
          @completedDomainSetup="selectedPanel = ''"
        ></add-domain-wizard>
      </div>
      <div v-else-if="selectedPanel == 'manage_subdomains'">
        <subdomains @clickedBackButton="deselectDomain" />
      </div>
      <div v-else-if="selectedPanel == 'manage_records'">
        <zone-records @clickedBackButton="deselectDomain" />
      </div>
    </div>
    <loading-overlay
      :is-loading="showLoader"
      :full-page="true"
    ></loading-overlay>
    <gd-modal name="domain-verification-modal" title="Verify Domain">
      <div class="flex flex-col p-4">
        <p class="text-dark antialiased mt-4" v-if="selectedDomain.type == 1">
          {{ $t('frontend_custom_domains_update_your_nameservers_desc') }}
        </p>
        <p class="text-dark antialiased mt-4" v-else>
          {{ $t('frontend_custom_domains_create_a_txt_record_desc') }}
        </p>
        <div class="flex flex-col text-grey-600 mt-8 w-full">
          <div v-for="(dnsRecord, index) in selectedDomainRecords" :key="index">
            <input-with-clipboard
              v-model="selectedDomainRecords[index]"
              class="mt-2"
            ></input-with-clipboard>
          </div>
        </div>
        <div class="flex flex-row w-full mb-6 mt-4 justify-end">
          <gd-button
            size="auto"
            class="px-4"
            variant="primary"
            @click="verifyDomainStatus"
            >{{ $t('frontend_gduikit_validate_dns') }}</gd-button
          >
        </div>
      </div>
    </gd-modal>
    <gd-modal
      name="root-domain-connector-modal"
      v-if="selectedRootDomainDomain"
      :title="'Choose Primary App For ' + selectedRootDomainDomain.domain_name"
    >
      <div class="flex flex-col p-4">
        <div class="flex flex-row w-full mx-2">
          <!-- TODO: use GdDropdown and brandData -->
          <select
            v-model="rootApplication.application"
            class="
              h-10
              outline-none
              rounded-lg
              border
              px-2
              border-grey-200
              mr-2
              w-full
            "
            :value="rootApplication.application"
            placeholder="Application"
          >
            <option value="pages">{{ brandApps?.pages?.title || 'GroovePages' }}</option>
            <option value="sell-funnel">{{ brandApps?.sell?.title || 'GrooveSell' }} Branding</option>
            <option value="sell-tracking">{{ brandApps?.sell?.title || 'GrooveSell' }} Tracking</option>
            <option value="membership">{{ brandApps?.member?.title || 'GrooveMember' }} Membership</option>
            <option value="portal">{{ brandApps?.member?.title || 'GrooveMember' }} Portal</option>
            <option value="blog">{{ brandApps?.blog?.title || 'GrooveBlog' }}</option>
            <option value="kart">{{ brandApps?.kart?.title || 'Groovekart' }}</option>
          </select>
        </div>
        <div class="flex flex-row w-full my-4 justify-end">
          <gd-button
            variant="primary"
            size="auto"
            class="ml-2 px-4"
            @click="saveRootdomain"
          >
            {{ $t('frontend_save') }}
          </gd-button>
        </div>
      </div>
    </gd-modal>
    <gd-modal name="domain-404-modal" title="Default 404 Error Page">
      <div class="flex flex-col p-4">
        <div class="flex flex-col text-grey-600 mt-8 w-full">
          <label class="font-semibold text-sm text-grey-700">
            {{ $t('frontend_custom_domains_url_to_br_redirected') }}
          </label>
          <text-input
            class="mt-2"
            v-model="selectedDomain.url_404"
            placeholder="Redirect URL"
          ></text-input>
        </div>
        <div class="flex flex-row w-full mb-6 mt-4 justify-end">
          <gd-button
            size="auto"
            class="px-4"
            variant="primary"
            @click="save404Url"
            >{{ $t('frontend_save') }}</gd-button
          >
        </div>
      </div>
    </gd-modal>
    <gd-modal
      name="groove_ai_verification_modal"
      :title="'Groove.ai verification link'"
    >
      <div class="flex flex-col items-center justify-center px-3 py-5">
        <div class="py-2 px-4 w-full mb-4">
          <text-input v-model="grooveAiVerificationLink"></text-input>
        </div>
        <div class="flex flex-col items-center justify-center">
          <gd-button
            variant="shadowPrimary"
            :disabled="!grooveAiVerificationLink"
            @click="fetchSubdomains(selectedDomain.id, 'Groove.ai')"
          >
            {{ 'Verify' }}
          </gd-button>
        </div>
      </div>
    </gd-modal>
    <upgrade-modal ref="upgradeModal" />
  </div>
</template>

<script>
import Auth from '@groovepages/gd-lib/auth';
import Notify from '../notify';
import { actions } from '@groovepages/gd-lib/userPermissions';
import UpgradeModal from '@groovepages/gd-ui-kit/components/UpgradeModal';
import { HalfCircleSpinner } from 'epic-spinners';
import Swal from 'sweetalert2';
import { twJoin } from 'tailwind-merge';
import { mapGetters } from 'vuex';
import CheckIcon from '../assets/check.svg';
import CloseIcon from '../assets/close-icon.svg';
import ClockIcon from '../assets/icons/clock.svg';
import HomeIcon from '../assets/icons/home.svg';
import PencilIcon from '../assets/icons/pencil.svg';
import TrashIcon from '../assets/trash.svg';
import domainStore from '../store';
import recordStore from '../store/records.js';
import AccountDomainRecordEditor from './AccountDomainRecordEditor';
import AddDomainWizard from './AddDomainWizard';
import GdButton from './Button';
import ConfirmModal from './ConfirmModal';
import InputWithClipboard from './InputWithClipboard';
import LoadingOverlay from './LoadingOverlay';
import GdModal from './Modal';
import Subdomains from './Subdomains';
import TextInput from './TextInput';
import ZoneRecords from './ZoneRecords';

export default {
  name: 'CustomDomains',
  components: {
    GdModal,
    HalfCircleSpinner,
    ConfirmModal,
    AccountDomainRecordEditor,
    GdButton,
    TextInput,
    AddDomainWizard,
    ZoneRecords,
    LoadingOverlay,
    Subdomains,
    InputWithClipboard,
    UpgradeModal,
    PencilIcon,
    TrashIcon,
    CheckIcon,
    CloseIcon,
    HomeIcon,
    ClockIcon
  },
  computed: {
    subDomain() {
      return (
        this.$store.getters.user.metadata &&
        this.$store.getters.user.metadata.subdomain
      );
    },
    freeUser() {
      return this.$store.getters.isLiteUser;
    },
    hostedDomains() {
      const domains = this.$store.getters.user.metadata.domainsList || {};
      return Object.keys(domains).filter(domain => domains[domain].hosted);
    },
    whoisLink() {
      return `https://whois.icann.org/en/lookup?name=${this.newDomain.domain}`;
    },
    ...mapGetters({
      domains: 'domains/domains',
      selectedDomain: 'domains/selectedDomain',
      selectedDomainRecords: 'domains/selectedDomainRecords'
    }),
    brandApps() {
      const brandData = this.$store.getters.brandData;
      if (brandData) {
        return brandData.apps.reduce((acc, app) => {
          if (app.slug) acc[app.slug] = app;
          return acc;
        }, {});
      }
      return null;
    }
  },
  data() {
    return {
      activeRowId: 0,
      showRootDomainConnector: false,
      selectedRootDomainDomain: null,
      rootApplication: {
        type: 1,
        application: 'pages',
        subdomain: null
      },
      isLoading: false,
      showLoader: false,
      domainStore,
      recordStore,
      selectedPanel: '',
      setUpDomain: false,
      setUpCustomDomain: false,
      showCname: false,
      setupGrooveDomain: false,
      errorMsg: '',
      newSubDomain: '',
      showCreateDomain: false,
      showRecordEditor: false,
      loadingRecords: false,
      savingRecord: false,
      togglingProxy: undefined,
      selectedRecord: undefined,
      deletingRecord: undefined,
      deletingDomain: undefined,
      selectedDomainZone: null,
      newDomain: {
        domain: '',
        hosted: true,
        platform: 'pages'
      },
      addingDomain: undefined,
      grooveSellLegacyDomains: [],
      grooveAiVerificationLink: ''
    };
  },
  created() {
    this.getUserDomains();
  },
  methods: {
    selectPanel(panel = '') {
      this.selectedPanel = panel;
    },
    openRootDomainConnectorModal(domain, index) {
      const userFeature = Auth.fetchFeatureValue(actions.CUSTOM_DOMAINS);
      if (userFeature != -1 && index >= userFeature) {
        this.$refs.upgradeModal.open();
      } else {
        this.showRootDomainConnector = true;
        this.selectedRootDomainDomain = domain;
        this.rootApplication.application = 'pages';
        this.$root.$emit('modal-open', 'root-domain-connector-modal');
      }
    },
    async open404UrlModal(id) {
      this.showLoader = true;

      try {
        await this.$store.dispatch('domains/getDomain', {
          id
        });
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
        this.$root.$emit('modal-open', 'domain-404-modal');
      }
    },

    async saveRootdomain() {
      if (
        this.rootApplication.application == 'ai' &&
        !this.rootApplication.key
      ) {
        Notify.fail('Please enter whitelabel key', 'bottom-right');
        return;
      }

      try {
        this.showLoader = true;
        const rootDomain = await this.$store.dispatch('domains/setRootDomain', {
          selectedDomain: this.selectedRootDomainDomain,
          data: this.rootApplication
        });

        if (rootDomain) {
          Notify.success(
            this.$t('frontend_custom_domains_successfully_connected_root'),
            'bottom-right'
          );
        } else {
          Notify.fail(
            this.$t('frontend_custom_domains_unable_to_connect_root'),
            'bottom-right'
          );
        }
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showRootDomainConnector = false;
        this.selectedRootDomainDomain = null;
        this.showLoader = false;
      }
    },

    async getUserDomains() {
      this.showLoader = true;
      try {
        await this.$store.dispatch('domains/getDomains');
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
      }
    },

    async openVerificationModal(id, type) {
      this.showLoader = true;

      try {
        await this.$store.dispatch('domains/getDomain', { id });
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
        if (type === 'ai')
          this.$root.$emit('modal-open', 'groove_ai_verification_modal');
        else this.$root.$emit('modal-open', 'domain-verification-modal');
      }
    },

    getAppProps(appSlug, domain) {
      const baseClass = 'w-8 h-8 focus:outline-none';
      switch (appSlug) {
        case 'pages': {
          const isEnabled = domain.subdomains.some(
            e => e.application === 'pages'
          );
          const app = this.brandApps?.pages;
          const matchedClass =
            domain.root_application?.application === 'pages'
              ? 'border-b-2 border-primary'
              : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
        case 'sell': {
          const isEnabled = domain.subdomains.some(e =>
            ['sell-funnel', 'sell-tracking'].includes(e.application)
          );
          const app = this.brandApps?.sell;
          const matchedClass = ['sell-funnel', 'sell-tracking'].includes(
            domain.root_application?.application
          )
            ? 'border-b-2 border-primary'
            : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
        case 'member': {
          const isEnabled = domain.subdomains.some(e =>
            ['membership', 'portal'].includes(e.application)
          );
          const app = this.brandApps?.member;
          const matchedClass = ['membership', 'portal'].includes(
            domain.root_application?.application
          )
            ? 'border-b-2 border-primary'
            : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
        case 'blog': {
          const isEnabled = domain.subdomains.some(
            e => e.application === 'blog'
          );
          const app = this.brandApps?.blog;
          const matchedClass =
            domain.root_application?.application === 'blog'
              ? 'border-b-2 border-primary'
              : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
        case 'kart': {
          const isEnabled = domain.subdomains.some(
            e => e.application === 'kart'
          );
          const app = this.brandApps?.kart;
          const matchedClass =
            domain.root_application?.application === 'kart'
              ? 'border-b-2 border-primary'
              : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
        case 'ai': {
          const isEnabled = domain.subdomains.some(e => e.application === 'ai');
          const app = this.brandApps?.ai;
          const matchedClass =
            domain.root_application?.application === 'ai'
              ? 'border-b-2 border-primary'
              : '';
          const disabledClass = isEnabled ? '' : 'opacity-50';
          const classes = twJoin(baseClass, matchedClass, disabledClass);
          return {
            src: app?.logo,
            class: classes
          };
        }
      }
    },

    deselectDomain() {
      this.selectedDomain = null;
      this.selectedDomainRecords = [];
      this.selectedPanel = '';
    },

    async verifyDomainStatus() {
      this.showLoader = true;

      try {
        const domainStatus = await this.$store.dispatch(
          'domains/verifyDNSRecord',
          { id: this.selectedDomain.id }
        );
        if (domainStatus == 'success') {
          Notify.success(
            this.$t('frontend_custom_domains_successfully_verified_name'),
            'bottom-right'
          );
          this.$root.$emit('modal-close', 'domain-verification-modal');
        } else if (domainStatus == 'pending') {
          Notify.warn(
            this.$t('frontend_custom_domains_domain_status_check_initiated'),
            'bottom-right'
          );
        } else if (domainStatus == 'invalid') {
          Notify.fail(
            this.$t('frontend_custom_domains_domain_verification_failed'),
            'bottom-right'
          );
        } else {
          Notify.fail(
            this.$t('frontend_custom_domains_domain_deleted_or_moved'),
            'bottom-right'
          );
        }
      } catch (e) {
        Notify.warn(
          this.$t('frontend_custom_domains_domain_status_check_initiated'),
          'bottom-right'
        );
      } finally {
        this.showLoader = false;
      }
    },

    async save404Url() {
      if (!this.validURL(this.selectedDomain.url_404)) {
        return false;
      }
      this.showLoader = true;

      try {
        const domain404Url = await this.$store.dispatch(
          'domains/domain404UrlUpdate',
          {
            id: this.selectedDomain.id,
            data: this.selectedDomain.url_404
          }
        );
        if (domain404Url == 'success') {
          Notify.success('Updated Successfully', 'bottom-right');
          this.$root.$emit('modal-close', 'domain-404-modal');
        } else {
          Notify.fail(
            this.$t('frontend_custom_domains_update_failed'),
            'bottom-right'
          );
        }
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
      }
    },

    async fetchDnsRecords(id) {
      this.showLoader = true;

      try {
        const domainRecords = await this.$store.dispatch(
          'records/getDnsRecords',
          id
        );
        if (domainRecords) {
          this.selectedPanel = 'manage_records';
        }
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
      }
    },

    async fetchSubdomains(id, application) {
      this.showLoader = true;

      try {
        const subdomains = await this.$store.dispatch(
          'subdomains/getSubdomains',
          {
            id,
            application
          }
        );
        if (subdomains) {
          this.selectedPanel = 'manage_subdomains';
        }
      } catch (e) {
        Notify.fail(e, 'bottom-right');
      } finally {
        this.showLoader = false;
      }
    },

    async deleteDomain(id) {
      const { value: accept } = await Swal.fire({
        title: this.$t('frontend_sure'),
        text: this.$t('frontend_non_revertable'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('frontend_delete_confirm')
      });
      if (accept) {
        try {
          this.showLoader = true;
          await this.$store.dispatch('domains/deleteDomain', {
            id
          });

          Notify.success(
            this.$t('frontend_custom_domains_successfully_deleted'),
            'bottom-right'
          );
        } catch (e) {
          Notify.fail(e, 'bottom-right');
        } finally {
          this.showLoader = false;
        }
      }
    },

    validURL(str) {
      const pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-z\\d_]*)?$',
        'i'
      ); // fragment locator
      return !!pattern.test(str);
    }
  }
};
</script>

<style scoped>
.app-icons-container .app-title {
  visibility: hidden;
}

.app-icons-container:hover .app-title {
  visibility: visible;
}

.app-icons-container:hover .overlay-div {
  background-color: rgba(0, 0, 0, 0.3);
}

.btn-editor:before {
  border-radius: inherit;
  position: absolute;
  box-sizing: border-box;
  left: -3px;
  right: -3px;
  bottom: -3px;
  top: -3px;
  border: 3px solid #fff;
  content: '';
  transform: scale(0.95);
  opacity: 0;
  transition: all 0.25s;
  z-index: -1;
  pointer-events: none;
}
.btn-editor:hover:before {
  opacity: 1;
  transform: scale(1);
}
.btn-editor {
  position: relative;
  padding: 0;
  font-size: 0;
  outline: none;
  width: 36px;
  height: 36px;
  border: none;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.7);
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position: 50%;
  border-radius: 50%;
}

.btn-edit {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 269 269' xmlns='http://www.w3.org/2000/svg' fill-rule='evenodd' clip-rule='evenodd' stroke-linejoin='round' stroke-miterlimit='1.414'%3E%3Cpath d='M161.36 56.337c-7.042-7.05-18.46-7.05-25.5 0l-6.373 6.38-89.243 89.338.023.023-2.812 2.82s-8.968 9.032-29.216 74.4c-.143.456-.284.91-.427 1.373-.36 1.172-.726 2.362-1.094 3.568a785.126 785.126 0 0 0-.988 3.25c-.28.922-.556 1.835-.84 2.778-.64 2.14-1.29 4.318-1.954 6.567-1.455 4.937-5.01 16.07-.99 20.1 3.87 3.882 15.12.467 20.043-.993a1275.615 1275.615 0 0 0 9.41-2.83c1.032-.314 2.058-.626 3.063-.935 1.27-.39 2.52-.775 3.75-1.157l1.09-.34c62.193-19.365 73.358-28.453 74.286-29.284l.01-.01.067-.06 2.88-2.886.192.193 89.244-89.336 6.373-6.382c7.04-7.048 7.04-18.476 0-25.525l-50.998-51.05zM103.4 219.782c-.08.053-.185.122-.297.193l-.21.133c-.076.047-.158.098-.245.15l-.243.148c-2.97 1.777-11.682 6.362-32.828 14.017-2.47.894-5.162 1.842-7.98 2.82l-30.06-30.092c.98-2.84 1.928-5.55 2.825-8.04 7.638-21.235 12.22-29.974 13.986-32.94l.12-.2c.063-.1.12-.196.175-.283l.126-.2c.07-.11.14-.217.192-.296l2.2-2.205 54.485 54.542-2.248 2.255zM263.35 56.337l-50.996-51.05c-7.04-7.048-18.456-7.048-25.498 0L174.108 18.05c-7.04 7.048-7.04 18.476 0 25.524l50.996 51.05c7.04 7.048 18.457 7.048 25.498 0l12.75-12.762c7.04-7.05 7.04-18.477 0-25.525z' fill='%23fff'/%3E%3C/svg%3E");
}

.app-icons-overlay .playWrapper {
  opacity: 0;
  position: absolute;
  z-index: 1;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
}

.app-icons-overlay:hover .playWrapper {
  opacity: 1;
}

.app-icons-overlay {
  padding: 4px;
}

.app-icons-disabled {
  filter: grayscale(1);
  opacity: 0.5;
}
</style>
